/**
 * Modal Component
 *
 * "Simplicity is the ultimate sophistication." - Steve Jobs
 *
 * A clean, focused modal. Just the essentials.
 */

import React from 'react';
import { cn } from '@/shared/utils/utils';
import { X } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from './VisuallyHidden';

interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  imagePreview?: boolean;
}

const sizes = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
};

export const Modal: React.FC<ModalProps> = ({
  open,
  onOpenChange,
  title,
  description,
  icon,
  children,
  size = 'md',
  className,
  imagePreview = false,
}) => {
  if (!open) return null;

  if (imagePreview) {
    return (
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Portal>
          {/* Backdrop */}
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" />

          {/* Modal */}
          <Dialog.Content
            className={cn(
              'fixed inset-0 z-50 m-auto',
              'h-fit max-h-[calc(100vh-4rem)]',
              'bg-background rounded-2xl shadow-2xl',
              'overflow-hidden',
              'flex flex-col',
              'w-fit',
              className,
            )}
          >
            {/* Accessibility (screen readers) */}
            <VisuallyHidden>
              <Dialog.Title>{title ?? 'Dialog'}</Dialog.Title>
              <Dialog.Description>{description ?? 'Dialog content'}</Dialog.Description>
            </VisuallyHidden>

            {/* Close button */}
            <Dialog.Close
              className="absolute right-4 top-4 z-10 p-2 rounded-full hover:bg-black/5 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5 text-foreground/60" />
            </Dialog.Close>

            {/* Title as direct child - comes first */}
            {title && (
              <h2 className="text-lg font-medium text-foreground px-6 pt-6 pb-4 border-b border-border/50">
                {title}
              </h2>
            )}

            {/* Children (image) as direct child - comes after title */}
            {children}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        {/* Backdrop */}
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" />

        {/* Modal */}
        <Dialog.Content
          className={cn(
            'fixed inset-0 z-50 m-auto',
            'w-[calc(100%-2rem)] h-fit max-h-[calc(100vh-4rem)]',
            sizes[size],
            'bg-background rounded-2xl shadow-2xl',
            'overflow-hidden',
            className,
          )}
        >
          {/* Accessibility (screen readers) */}
          <VisuallyHidden>
            <Dialog.Title>{title ?? 'Dialog'}</Dialog.Title>
            <Dialog.Description>{description ?? 'Dialog content'}</Dialog.Description>
          </VisuallyHidden>

          {/* Close button */}
          <Dialog.Close
            className="absolute right-4 top-4 z-10 p-2 rounded-full hover:bg-black/5 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-foreground/60" />
          </Dialog.Close>

          {/* Header */}
          {(title || description || icon) && (
            <div className="px-6 pt-6 pb-4 border-b border-border/50">
              <div className="flex items-start gap-4 pr-8">
                {icon && (
                  <div className="shrink-0 w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    {icon}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  {title && (
                    <h2 className="text-lg font-medium text-foreground">
                      {title}
                    </h2>
                  )}
                  {description && (
                    <p className="mt-1 text-sm text-muted-foreground">
                      {description}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Body */}
          <div className={cn(
            'overflow-y-auto max-h-vh-60',
            (title || description || icon) ? 'p-6' : 'p-6',
          )}>
            {children}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
